<template>
    <div
        data-cy="event-property-filter"
        class="event-property-filter"
        :aria-labelledby="`event-property-multiselect-type-${id} event-property-multiselect-operator-${id} event-property-multiselect-value-${id}`">
        <event-property-filter-multiselect
            :id="`event-property-multiselect-type-${id}`"
            ref="eventPropertyMultiselect"
            class="event-property-multiselect"
            :aria-label="$t('eventPropertyFilter.Event property type')"
            :value="eventPropertySelection"
            :options="eventPropertyOptions"
            :value-label="eventPropertySelection.name"
            :multiselect-config="eventPropertyMultiselectConfig"
            :disabled="disabled"
            :applicable="isFilterApplicable"
            @input="onEventPropertySelection"
            @open="onMultiselectToggled('eventPropertyMultiselect')"
            @closed="onMultiselectToggled('eventPropertyMultiselect')" />
        <event-property-filter-multiselect
            v-if="field"
            :id="`event-property-multiselect-operator-${id}`"
            ref="operatorMultiselect"
            data-cy="operator-multiselect"
            class="operator-multiselect"
            :aria-label="$t('eventPropertyFilter.Event property operator')"
            :value="operatorSelection"
            :options="operatorOptions"
            :value-label="operatorSelection.name"
            :multiselect-config="operatorMultiselectConfig"
            :disabled="disabled"
            :applicable="isFilterApplicable"
            :schema="schema"
            @input="onOperatorSelection"
            @open="onMultiselectToggled('operatorMultiselect')"
            @closed="onMultiselectToggled('operatorMultiselect')" />
        <event-property-value-multiselect
            v-if="showEventPropertyValueTag"
            :id="`event-property-multiselect-value-${id}`"
            ref="eventPropertyValueMultiselect"
            data-cy="event-property-value-multiselect"
            class="event-property-value-multiselect"
            :aria-label="$t('eventPropertyFilter.Event property values')"
            :value="comparisonValueSelection"
            :options="eventPropertyValueOptions"
            :loading="loadingValueOptions"
            :multiple="operatorSupportsMultipleValues"
            :disabled="disabled"
            :applicable="isFilterApplicable"
            :is-metadata="isMetadata"
            :schema="schema"
            :element-type="elementType"
            @input="onEventPropertyValueSelection"
            @input-value-change="debouncedOnEventPropertyValueInputValueChange"
            @open="onMultiselectToggled('eventPropertyValueMultiselect')"
            @closed="onMultiselectToggled('eventPropertyValueMultiselect')" />
        <pendo-button
            v-if="isFilterValid"
            class="delete-button"
            icon="x"
            theme="app"
            type="tertiary"
            size="mini"
            icon-size="12px"
            :disabled="disabled"
            :aria-label="$t('eventPropertyFilter.Remove filter')"
            @click="deleteFilter" />
        <pendo-icon
            v-if="!isFilterApplicable || hasUtmWarning"
            v-pendo-tooltip="tooltipConfig"
            class="filter-warning-icon"
            type="alert-triangle"
            size="16" />
    </div>
</template>

<script>
import { computed, toRef } from 'vue';
import { mapGetters } from 'vuex';
import { PendoButton, PendoNotification, PendoIcon, PendoTooltip } from '@pendo/components';
import { MOBILE_QUERY_FIELDS, queryMetadataValues, isMobileDeviceProp } from '@/vuex/modules/metadata/metadata.utils';
import { runAggregationOnce } from '@utils/aggregation';
import EventPropertyFilterMultiselect from './event-property-filter-multiselect.vue';
import EventPropertyValueMultiselect from './event-property-value-multiselect.vue';
import { getOperatorsOptions, operatorSupportsMultipleValues, operatorNeedsValue, isFilterValid } from './utils';
import useEventPropertySelection from './use-event-property-selection';
import { getPlatformFromAppIds, deviceValuesByField } from '@/constants/device-props';
import { FilterType } from '@/types/analytics/analytics.types';
import {
    AUTOMATIC_GROUPS,
    VALID_UTM_DATE_IN_MS,
    VALID_DATE_FOR_ALL_IN_MS,
} from '@/web-analytics/utils/web-analytics.utils';
import union from 'lodash/union';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import moment from '@utils/moment-wrapper';
import isString from 'lodash/isString';
import SampleValuesAggregation from './sample-values.aggregation';
import { useId } from 'vue';

export default {
    name: 'EventPropertyFilter',
    components: {
        EventPropertyFilterMultiselect,
        EventPropertyValueMultiselect,
        PendoButton,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        eventPropertyOptions: {
            type: Array,
            required: true,
        },
        filter: {
            type: Object,
            default: () => ({
                field: '',
                kind: '',
                operator: '',
                comparisonValues: [],
            }),
        },
        appIds: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        metadata: {
            type: Object,
            default: () => ({}),
        },
        historicalMetadata: {
            type: Array,
            default: () => [],
        },
        sessionProperties: {
            type: Array,
            default: () => [],
        },
        accountMetadata: {
            type: Array,
            default: () => [],
        },
        visitorMetadata: {
            type: Array,
            default: () => [],
        },
        parentAccountMetadata: {
            type: Array,
            default: () => [],
        },
        selectedDateRange: {
            type: Object,
            default: () => ({}),
        },
        targetEntities: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['save', 'delete', 'filter-complete', 'filter-incomplete'],
    setup (props) {
        const id = useId();
        const filter = toRef(props, 'filter');
        const appIds = toRef(props, 'appIds');
        const metadata = toRef(props, 'metadata');
        const historicalMetadata = toRef(props, 'historicalMetadata');
        const sessionProperties = toRef(props, 'sessionProperties');
        const accountMetadata = toRef(props, 'accountMetadata');
        const visitorMetadata = toRef(props, 'visitorMetadata');
        const parentAccountMetadata = toRef(props, 'parentAccountMetadata');
        const platform = computed(() => getPlatformFromAppIds(appIds.value));
        const { field, kind, group, filterType, eventPropertySelection, name } = useEventPropertySelection(
            filter,
            platform,
            metadata,
            historicalMetadata,
            sessionProperties,
            accountMetadata,
            visitorMetadata,
            parentAccountMetadata,
        );

        return { field, kind, group, filterType, platform, eventPropertySelection, name, id };
    },
    data () {
        return {
            dirty: false,
            showEventPropertyValueTag: false,
            operator: this.filter.operator,
            comparisonValueSelection: this.filter.comparisonValues,
            loadingValueOptions: false,
            fetchedSampleValues: [],
            sampleValuesFilterString: undefined,
            eventPropertyValueOptions: [],
            schema: undefined,
            elementType: undefined,
            sampleValuesAggLimit: 500,
        };
    },
    computed: {
        ...mapGetters({
            getAppById: 'apps/getAppById',
        }),
        operatorOptions () {
            return getOperatorsOptions(this.eventPropertySelection.eventPropertySchema);
        },
        operatorSelection: {
            get () {
                return this.operatorOptions.find((opt) => opt.label === this.operator) || {};
            },
            set (value) {
                this.operator = value.label;
            },
        },
        operatorSupportsMultipleValues () {
            return operatorSupportsMultipleValues(this.operator);
        },
        isFilterValid () {
            return isFilterValid({
                field: this.field,
                operator: this.operator,
                comparisonValues: this.comparisonValueSelection,
            });
        },
        startOfDateRange () {
            const start = this.selectedDateRange?.start;
            if (!start) return null;

            return moment(start, 'YYYY-MM-DD').valueOf();
        },
        channelOrReferrerSelected () {
            return (
                this.eventPropertySelection?.filterType === FilterType.Utm &&
                AUTOMATIC_GROUPS.includes(this.eventPropertySelection?.eventPropertyField)
            );
        },
        utmWarning () {
            if (!this.channelOrReferrerSelected) return '';
            if (!this.startOfDateRange) return '';
            // if start date is after June 30, 2025, we know the data is valid
            if (this.startOfDateRange > VALID_DATE_FOR_ALL_IN_MS) return '';
            // if start date is before May 1, 2025, we show the session properties warning
            if (this.startOfDateRange < VALID_UTM_DATE_IN_MS) {
                return this.$t(
                    'eventPropertyFilter.Data for session properties is available starting May 2025 only. Channel and referrer automatic groups were updated in June 2025. Earlier data is available but may be less complete for some users.',
                );
            }
            // if start date is on or after May 1, 2025, we show the automatic groups warning
            if (this.startOfDateRange >= VALID_UTM_DATE_IN_MS) {
                return this.$t(
                    'eventPropertyFilter.Channel and referrer automatic groups were updated in June 2025. Earlier data is available but may be less complete for some users.',
                );
            }

            return '';
        },
        hasUtmWarning () {
            return this.utmWarning !== '';
        },
        isFilterApplicable () {
            if (!this.eventPropertyOptions.length) return true;

            const optionsListMatcher = (group) => {
                const { eventProps = [] } =
                    this.eventPropertyOptions.find(({ eventType }) => eventType === group) || {};

                return eventProps.some(
                    ({ eventPropertyField, eventPropertyKind }) =>
                        this.kind === eventPropertyKind && this.field === eventPropertyField,
                );
            };

            switch (this.filterType) {
                case FilterType.Custom:
                    return optionsListMatcher('Custom');
                case FilterType.Device:
                    return optionsListMatcher('Device Data');
                case FilterType.EventTopLevel:
                    return optionsListMatcher('Event Data');
                case FilterType.Location:
                    return optionsListMatcher('Location Data');
                case FilterType.HistoricalMetadata:
                    return optionsListMatcher('Historical Metadata');
                case FilterType.Utm:
                    if (this.hasUtmWarning) {
                        return false;
                    }

                    return optionsListMatcher('Session Properties');
                case FilterType.Account:
                    return optionsListMatcher('Account Metadata');
                case FilterType.Visitor:
                    return optionsListMatcher('Visitor Metadata');
                case FilterType.ParentAccount:
                    return optionsListMatcher('Parent Account Metadata');
                default:
                    return true;
            }
        },
        isMetadata () {
            return [
                FilterType.HistoricalMetadata,
                FilterType.Utm,
                FilterType.Account,
                FilterType.Visitor,
                FilterType.ParentAccount,
            ].includes(this.eventPropertySelection?.filterType);
        },
        needsEventPropertyValue () {
            return this.showEventPropertyValueTag && !this.comparisonValueSelection.length;
        },
        tooltipConfig () {
            if (this.hasUtmWarning) {
                return {
                    content: this.utmWarning,
                    multiline: true,
                    arrow: true,
                };
            }

            return {
                content: this.$t(
                    'eventPropertyFilter.This filter might have undesirable impact because the associated event has been removed.',
                ),
                multiline: true,
                arrow: true,
            };
        },
    },
    created () {
        this.openMultiselects = {
            eventPropertyMultiselect: false,
            operatorMultiselect: false,
            eventPropertyValueMultiselect: false,
        };
        this.eventPropertyMultiselectConfig = {
            placeholder: this.$t('eventPropertyFilter.Filter by...'),
            showSearch: true,
            allowEmpty: false,
            groupOptionsKey: 'eventProps',
            groupLabelKey: 'groupLabel',
            valueKey: 'eventPropertyFieldKey',
            labelKey: 'name',
        };
        this.operatorMultiselectConfig = {
            valueKey: 'label',
            labelKey: 'name',
            allowEmpty: false,
        };
        this.showEventPropertyValueTag = operatorNeedsValue(this.operator);
        if (!this.filter.field) {
            this.toggleMultiselect('eventPropertyMultiselect');
        }
    },
    methods: {
        onEventPropertySelection (value) {
            this.dirty = true;
            this.eventPropertyValueOptions = [];
            this.eventPropertySelection = value;
            this.name = value.name;
            this.operatorSelection = this.operatorOptions[0];
            this.name = value.name;
            this.comparisonValueSelection = [];
            this.showEventPropertyValueTag = false;
            this.toggleMultiselect('operatorMultiselect');
            if (operatorNeedsValue(this.operator)) {
                this.showEventPropertyValueTag = true;
            }
            this.saveFilter();
        },
        onOperatorSelection (value) {
            this.dirty = true;
            this.operatorSelection = value;
            this.showEventPropertyValueTag = operatorNeedsValue(this.operator);
            if (!this.showEventPropertyValueTag) {
                this.comparisonValueSelection = [];
            } else if (
                !this.comparisonValueSelection.length ||
                (!this.operatorSupportsMultipleValues && this.comparisonValueSelection.length > 1)
            ) {
                this.comparisonValueSelection = [];
            }
            this.saveFilter();
        },
        onEventPropertyValueSelection (value) {
            // Remove any trailing or leading whitespace from the value.
            const trimmedValue = value.map((value) => {
                return isString(value) ? value.trim() : value;
            });
            this.dirty = true;
            this.comparisonValueSelection = trimmedValue;
            this.saveFilter();
        },
        debouncedOnEventPropertyValueInputValueChange: debounce(
            function (value) {
                this.onEventPropertyValueInputValueChange(value);
            },
            500,
            {
                leading: true,
                trailing: false,
            },
        ),
        async toggleMultiselect (ref) {
            await this.$nextTick();
            this.$refs[ref]?.toggleMenu();
        },
        async onMultiselectToggled (multiselect) {
            this.openMultiselects[multiselect] = !this.openMultiselects[multiselect];
            if (
                multiselect === 'operatorMultiselect' &&
                !this.openMultiselects.operatorMultiselect &&
                this.needsEventPropertyValue &&
                !this.openMultiselects.eventPropertyMultiselect
            ) {
                this.toggleMultiselect('eventPropertyValueMultiselect');

                return;
            }

            if (multiselect === 'operatorMultiselect' && this.openMultiselects.operatorMultiselect) {
                await this.$nextTick();
                this.$refs.operatorMultiselect.getTriggerRef().$el.focus();

                return;
            }

            // If no multiselects are open, the user is done editing the filter, and we should validate it.
            if (Object.values(this.openMultiselects).every((multiselect) => !multiselect)) {
                this.checkFilter();
                // Emit event when all dropdowns are closed and filter is valid
                if (this.isFilterValid) {
                    this.$emit('filter-complete', { filter: this.filter });
                } else {
                    this.$emit('filter-incomplete');
                }
            } else if (
                multiselect === 'eventPropertyValueMultiselect' &&
                this.openMultiselects.eventPropertyValueMultiselect
            ) {
                await this.setEventPropertyValueOptions();
            }
        },
        async fetchValues (kind, group, field, params) {
            this.loadingValueOptions = true;
            this.sampleValuesFilterString = params?.filter || '';
            try {
                const {
                    data: { values },
                } = await queryMetadataValues(kind, group, field, params);

                return values.filter(Boolean);
            } catch (error) {
                console.error(error);
                PendoNotification({
                    type: 'error',
                    title: 'Unable to fetch unique values.',
                    duration: 3000,
                });
            } finally {
                this.loadingValueOptions = false;
            }
        },
        async onEventPropertyValueInputValueChange (value) {
            if (value !== undefined && this.sampleValuesFilterString === value) {
                return;
            }
            const newValShorter =
                this.sampleValuesFilterString !== undefined &&
                this.sampleValuesFilterString !== '' &&
                value !== undefined &&
                this.sampleValuesFilterString.length >= value.length;

            const {
                filterType,
                eventPropertyKind: kind,
                eventPropertyField: field,
                eventPropertyGroup: group,
                eventPropertySchema,
            } = this.eventPropertySelection;
            let uniqueValues = this.eventPropertySelection.uniqueValues
                ? this.eventPropertySelection.uniqueValues.slice()
                : [];
            // Remove any trailing or leading whitespace from the unique values array
            uniqueValues = uniqueValues.map((value) => {
                return isString(value) ? value.trim() : value;
            });
            const skipFetch = uniqueValues?.length > 0 && uniqueValues?.length < 300;

            const constantDevicePropValues =
                filterType === FilterType.Device ? deviceValuesByField({ platform: this.platform })[field] : [];
            const isFetchableHistoricalMetadata =
                filterType === FilterType.HistoricalMetadata && eventPropertySchema === 'string';
            const isFetchableMobileDeviceProp =
                MOBILE_QUERY_FIELDS[field] && this.appIds.some((appId) => {
                    const app = this.getAppById(appId);

                    return isMobileDeviceProp({ app, field });
                });
            const isFetchableMetadata =
                [FilterType.Visitor, FilterType.Account, FilterType.ParentAccount].includes(filterType) &&
                eventPropertySchema === 'string';
            const isCustomEventProp = filterType === FilterType.Custom;
            const fetchValuesByAggregation = [
                FilterType.Location,
                FilterType.PageParameter,
                FilterType.EventTopLevel,
            ].includes(filterType);

            if (!skipFetch && (fetchValuesByAggregation || isCustomEventProp)) {
                if (value && !newValShorter && this.fetchedSampleValues.length < this.sampleValuesAggLimit) {
                    // If we have less than sampleValuesAggLimit we didn't hit the limit the first time. Skip fetching filtered sample values.
                    return;
                }
                this.fetchSampleValuesAndSetOptions(filterType, field, group, kind, value);
            } else if (isFetchableMetadata || isFetchableHistoricalMetadata) {
                const params = {
                    app: this.appIds.join(','),
                };
                if (value) {
                    params.filter = value;
                }
                const fetchValues = await this.fetchValues(kind, group, field, params);
                let options = union(uniqueValues, fetchValues).filter((value) => !isNil(value) && value !== '');
                if (eventPropertySchema === 'list') {
                    options = uniq(options.flat());
                }
                this.eventPropertyValueOptions = options;
            } else if (isFetchableMobileDeviceProp) {
                uniqueValues = [];
                const params = {
                    app: this.appIds.join(','),
                };
                if (value) {
                    params.filter = value;
                }
                const fetchValues = await this.fetchValues('visitor', 'auto', MOBILE_QUERY_FIELDS[field], {
                    app: this.appIds.join(','),
                });
                let options = union(constantDevicePropValues, uniqueValues, fetchValues).filter(
                    (value) => !isNil(value) && value !== '',
                );
                if (eventPropertySchema === 'list') {
                    options = uniq(options.flat());
                }
                this.eventPropertyValueOptions = options;
            } else {
                let options = union(constantDevicePropValues, uniqueValues).filter(
                    (value) => !isNil(value) && value !== '',
                );
                if (eventPropertySchema === 'list') {
                    options = uniq(options.flat());
                }
                this.eventPropertyValueOptions = options;
            }
        },
        async setEventPropertyValueOptions () {
            const { eventPropertySchema, eventPropertyElementType } = this.eventPropertySelection;

            await this.onEventPropertyValueInputValueChange(undefined);
            this.schema = eventPropertySchema;
            this.elementType = !isEmpty(eventPropertyElementType) ? eventPropertyElementType : 'string';
        },
        async getSampleValuesFromAggregation (filterType, field, group, kind, filterString) {
            this.loadingValueOptions = true;
            this.sampleValuesFilterString = filterString || '';
            this.fetchedSampleValues = [];

            let fetchValues;
            try {
                const aggResponse = await runAggregationOnce(SampleValuesAggregation, {
                    filterType,
                    kind,
                    field,
                    group,
                    targetEntities: this.targetEntities,
                    filterString,
                    limit: this.sampleValuesAggLimit,
                });
                if (filterType === FilterType.Location && field === 'region') {
                    fetchValues = aggResponse?.sampleValues?.map((value) => value.toUpperCase());
                } else {
                    fetchValues = aggResponse?.sampleValues;
                }
                this.fetchedSampleValues = fetchValues || [];
            } catch (error) {
                console.error(error);
                PendoNotification({
                    type: 'error',
                    title: this.$t('eventPropertyFilter.Unable to fetch sample values.'),
                    duration: 3000,
                });
            } finally {
                this.loadingValueOptions = false;
            }
        },
        async fetchSampleValuesAndSetOptions (filterType, field, group, kind, filterString) {
            await this.getSampleValuesFromAggregation(filterType, field, group, kind, filterString);

            this.eventPropertyValueOptions = union(this.eventPropertyValueOptions, this.fetchedSampleValues).filter(
                (value) => !isNil(value) && value !== '',
            );
        },
        checkFilter () {
            if (!this.isFilterValid) {
                this.deleteFilter();
            }
        },
        saveFilter () {
            if (!this.dirty) return;
            const filter = {
                field: this.field,
                kind: this.kind,
                group: this.group,
                filterType: this.filterType,
                operator: this.operator,
                comparisonValues: this.comparisonValueSelection,
                id: this.filter.id,
                name: this.name,
                valid: this.isFilterValid,
            };
            this.$emit('save', { filter });
            this.dirty = false;
        },
        deleteFilter () {
            const { filter } = this;
            this.$emit('delete', { filter });
        },
    },
};
</script>

<style lang="less" scoped>
.event-property-filter {
    display: grid;
    grid-template-columns: minmax(0, max-content) repeat(auto-fit, minmax(0, max-content));
    gap: 2px;

    .event-property-multiselect {
        overflow: hidden;
    }

    .delete-button {
        font-size: 12px;
        display: flex;
        height: 24px;
        padding: 4px 6px !important;
        justify-content: center;
        align-items: center;
        color: var(--palette-teal-80);
        background-color: rgba(var(--raw-palette-teal-80), 0.1);
        border-radius: 0px 5px 5px 0px;

        &:hover {
            color: var(--palette-teal-90);
            background-color: rgba(var(--raw-palette-teal-70), 0.3);
        }
    }

    .filter-warning-icon {
        margin-left: 4px;
        color: var(--palette-yellow-100);
        align-content: center;
    }
}
</style>
